const React = require("react");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { PageHeader, Button, Kpi, ResourceState, StatusBadge } = require("../components/ui");
const h = React.createElement;

const CJ_PLATFORM_SLUG = "cj-dropshipping";

const DropshippingAdmin = () => {
  const { api, t, isFamily, userId } = useCJDropshipping();
  const resource = useDropshippingResource(async () => {
    if (!isFamily) return null;
    const [overview, connections, platforms, environment] = await Promise.all([
      api.getAdminOverview(), api.getAdminConnections({ limit: 100 }), api.listPlatforms(),
      api.getPlatformEnvironmentSettings(),
    ]);
    return {
      overview: overview.overview,
      connections: connections.connections,
      platforms: platforms.platforms,
      dropshippingOut: environment.settings?.dropshippingOut || null,
    };
  }, [api, isFamily]);
  const [form, setForm] = React.useState({ name: "", slug: "", websiteUrl: "", apiBaseUrl: "", apiDocUrl: "", authType: "api_key", status: "active" });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [envBusy, setEnvBusy] = React.useState(false);
  const [envError, setEnvError] = React.useState("");
  const change = (key) => (event) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const create = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try { await api.createPlatform(form); setForm({ name: "", slug: "", websiteUrl: "", apiBaseUrl: "", apiDocUrl: "", authType: "api_key", status: "active" }); await resource.reload(); }
    catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  };
  const toggle = async (platform) => {
    setBusy(true); setError("");
    try { await api.updatePlatform(platform._id, { status: platform.status === "active" ? "inactive" : "active" }); await resource.reload(); }
    catch (requestError) { setError(requestError.message); }
    finally { setBusy(false); }
  };
  const toggleEnvironmentMode = async (currentMode) => {
    setEnvBusy(true); setEnvError("");
    try {
      const dropshippingOut = resource.data?.dropshippingOut || {};
      const nextMode = currentMode === "test" ? "live" : "test";
      await api.updatePlatformEnvironmentMode(userId, {
        ...dropshippingOut,
        platformModes: { ...(dropshippingOut.platformModes || {}), [CJ_PLATFORM_SLUG]: nextMode },
      });
      await resource.reload();
    } catch (requestError) {
      setEnvError(requestError.message);
    } finally {
      setEnvBusy(false);
    }
  };
  if (!isFamily) return h("div", { className: "cjds-state" }, h("h3", null, t("h_cj_admin_only", "Family access required")));
  const data = resource.data || {};
  const overview = data.overview || {};
  const environmentMode = data.dropshippingOut?.platformModes?.[CJ_PLATFORM_SLUG] === "test" ? "test" : "live";
  const fields = [
    ["name", "h_cj_platform_name", "Platform name"],
    ["slug", "h_cj_platform_slug", "Slug"],
    ["websiteUrl", "h_cj_website", "Website URL"],
    ["apiBaseUrl", "h_cj_api_base", "API base URL"],
    ["apiDocUrl", "h_cj_api_docs_url", "API documentation URL"],
  ];
  const platformCards = (data.platforms || []).map((platform) => h(
    "article",
    { className: "cjds-card", key: platform._id },
    h("div", { className: "cjds-card-body" },
      h("div", { className: "cjds-row cjds-between" },
        h("strong", null, platform.name),
        h(StatusBadge, { status: platform.status, t })
      ),
      h(Button, { variant: "secondary", disabled: busy, onClick: () => toggle(platform) },
        platform.status === "active" ? t("h_cj_deactivate", "Deactivate") : t("h_cj_activate", "Activate")
      )
    )
  ));
  const connectionRows = (data.connections || []).map((connection) => h(
    "tr",
    { key: connection._id },
    h("td", null, connection.sellerId?.email || "—"),
    h("td", null, connection.platformId?.name || "—"),
    h("td", null, h(StatusBadge, { status: connection.status, t })),
    h("td", null, connection.lastSyncAt ? new Date(connection.lastSyncAt).toLocaleString() : "—")
  ));
  return h(React.Fragment, null,
    h(PageHeader, { title: t("h_cj_admin_title", "Dropshipping administration"), description: t("h_cj_admin_desc", "Manage platforms, connected sellers, imports and API health.") }),
    error ? h("div", { className: "cjds-alert", role: "alert" }, error) : null,
    h(ResourceState, { loading: resource.loading, error: resource.error, empty: false, onRetry: resource.reload, t },
      h(React.Fragment, null,
        h("div", { className: "cjds-kpis" },
          h(Kpi, { value: overview.connections || 0, label: t("h_cj_connections", "Connections") }),
          h(Kpi, { value: overview.products || 0, label: t("h_cj_products_count", "Products") }),
          h(Kpi, { value: overview.imports || 0, label: t("h_cj_imports", "Imports") }),
          h(Kpi, { value: overview.errors || 0, label: t("h_cj_errors", "Errors") })
        ),
        h("article", { className: "cjds-card" },
          h("div", { className: "cjds-card-body" },
            h("div", { className: "cjds-row cjds-between" },
              h("strong", null, t("h_cj_environment_title", "CJ Dropshipping environment")),
              h("span", { className: environmentMode === "test" ? "cjds-badge pending" : "cjds-badge" },
                environmentMode === "test" ? t("h_cj_environment_test", "TEST") : t("h_cj_environment_live", "LIVE"))
            ),
            h("p", { className: "cjds-muted" }, t(
              "h_cj_environment_desc",
              "TEST shows a warning banner to sellers on this platform (verification environment). LIVE is normal production."
            )),
            envError ? h("div", { className: "cjds-alert", role: "alert" }, envError) : null,
            h(Button, { variant: "secondary", disabled: envBusy, onClick: () => toggleEnvironmentMode(environmentMode) },
              envBusy
                ? t("h_cj_environment_saving", "Saving…")
                : environmentMode === "test"
                  ? t("h_cj_environment_switch_to_live", "Switch to LIVE")
                  : t("h_cj_environment_switch_to_test", "Switch to TEST")
            )
          )
        ),
        h("form", { className: "cjds-form", onSubmit: create },
          h("h3", null, t("h_cj_add_platform", "Add a platform")),
          ...fields.map(([key, textKey, label]) => h("div", { className: "cjds-field", key },
            h("label", { htmlFor: `cj-admin-${key}` }, t(textKey, label)),
            h("input", { id: `cj-admin-${key}`, className: "cjds-input", value: form[key], required: ["name", "slug", "apiBaseUrl"].includes(key), onChange: change(key) })
          )),
          h(Button, { type: "submit", disabled: busy }, t("h_cj_add", "Add"))
        ),
        h("h3", null, t("h_cj_platforms_title", "Dropshipping platforms")),
        h("div", { className: "cjds-grid" }, ...platformCards),
        h("h3", null, t("h_cj_connected_sellers", "Connected sellers")),
        h("div", { className: "cjds-table-wrap" },
          h("table", { className: "cjds-table" },
            h("thead", null, h("tr", null,
              h("th", null, t("h_cj_seller", "Seller")),
              h("th", null, t("h_cj_platform", "Platform")),
              h("th", null, t("h_cj_status", "Status")),
              h("th", null, t("h_cj_last_sync", "Last sync"))
            )),
            h("tbody", null, ...connectionRows)
          )
        )
      )
    )
  );
};
module.exports = { DropshippingAdmin };
